import { defineComponent as _defineComponent } from 'vue'
import { unref as _unref, createVNode as _createVNode, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, createElementVNode as _createElementVNode, toDisplayString as _toDisplayString, renderList as _renderList, Fragment as _Fragment, createBlock as _createBlock, withCtx as _withCtx } from "vue"

const _hoisted_1 = { class: "global-search-container relative w-full" }
const _hoisted_2 = { class: "flex items-center" }
const _hoisted_3 = {
  key: 0,
  class: "flex items-center justify-center p-4"
}
const _hoisted_4 = { class: "text-muted-foreground ml-2 text-sm" }
const _hoisted_5 = {
  key: 2,
  class: "text-muted-foreground p-4 text-center text-sm"
}

import { ref, onMounted, onUnmounted, nextTick, watch } from 'vue';
    import { useGlobalSearch } from '@/composables/useGlobalSearch';
    import { Input } from '@/components/ui/input';
    import { Card, CardContent } from '@/components/ui/card';
    import { LoaderCircle, Search, X } from 'lucide-vue-next';
    import GlobalSearchResult from '@/components/GlobalSearchResult.vue';
    import { useI18n } from 'vue-i18n';
    import { router } from '@inertiajs/vue3';
    import { route } from 'ziggy-js';

    
export default /*@__PURE__*/_defineComponent({
  __name: 'GlobalSearch',
  setup(__props) {

    const { t } = useI18n();
    const inputRef = ref<InstanceType<typeof Input>>();
    const resultsRef = ref<HTMLElement>();
    const isDropdownOpen = ref(false);
    const selectedIndex = ref(-1);

    const {
        searchQuery,
        searchResults,
        isLoading,
        isFetching,
        hasResults,
        isMinimumQuery,
        setSearchQuery,
        clearSearch,
    } = useGlobalSearch();

    // Computed property to check if result has a meaningful name
    const hasResultName = (result: any) => {
        return (
            result.name &&
            result.name.trim() !== '' &&
            result.name !== null &&
            result.name !== undefined
        );
    };

    // Helper function for highlighting text
    const highlightText = (text: string, query: string): string => {
        if (!query) return text;
        const regex = new RegExp(`(${query.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')})`, 'gi');
        return text.replace(
            regex,
            '<mark class="bg-primary/20 text-primary px-1 rounded">$1</mark>',
        );
    };

    // Handle input changes
    const handleInput = (event: Event) => {
        const target = event.target as HTMLInputElement;
        setSearchQuery(target.value);

        if (target.value.length >= 2) {
            isDropdownOpen.value = true;
            selectedIndex.value = -1;
        } else {
            isDropdownOpen.value = false;
        }
    };

    // Handle keyboard navigation
    const handleKeyDown = (event: KeyboardEvent) => {
        if (!isDropdownOpen.value || !hasResults.value) return;

        // Count total results including all contexts
        const totalResults =
            searchResults.value?.results?.reduce((count, result) => {
                return count + (result.contexts?.length || 1);
            }, 0) || 0;

        switch (event.key) {
            case 'ArrowDown':
                event.preventDefault();
                selectedIndex.value = Math.min(selectedIndex.value + 1, totalResults - 1);
                scrollToSelected();
                break;
            case 'ArrowUp':
                event.preventDefault();
                selectedIndex.value = Math.max(selectedIndex.value - 1, -1);
                scrollToSelected();
                break;
            case 'Enter':
                event.preventDefault();
                if (selectedIndex.value >= 0) {
                    navigateToSelectedResult();
                }
                break;
            case 'Escape':
                event.preventDefault();
                closeDropdown();
                break;
        }
    };

    // Scroll to selected item
    const scrollToSelected = async () => {
        await nextTick();
        if (resultsRef.value && selectedIndex.value >= 0) {
            const allElements = resultsRef.value.querySelectorAll('a, .result-item');
            const selectedElement = allElements[selectedIndex.value] as HTMLElement;
            selectedElement?.scrollIntoView({ block: 'nearest' });
        }
    };

    // Navigate to selected result
    const navigateToSelectedResult = () => {
        if (!searchResults.value?.results) return;

        let currentIndex = 0;
        for (const result of searchResults.value.results) {
            if (result.contexts && result.contexts.length > 0) {
                for (const context of result.contexts) {
                    if (currentIndex === selectedIndex.value) {
                        navigateToResult(context);
                        return;
                    }
                    currentIndex++;
                }
            } else {
                if (currentIndex === selectedIndex.value) {
                    // Fallback for results without contexts
                    if (result.url) {
                        navigateToResult(result);
                    }
                    return;
                }
                currentIndex++;
            }
        }
    };

    // Navigate to search result
    const navigateToResult = (result: any) => {
        // Check if company switching is needed
        if (result.company_switch) {
            // First switch company, then navigate to the target URL
            router.patch(
                route('v2.dashboard.selected.set'),
                {
                    selected_company_id: result.company_switch,
                },
                {
                    preserveScroll: true,
                    onSuccess: () => {
                        // After company switch, navigate to the target page
                        router.visit(result.url);
                    },
                },
            );
        } else {
            // Direct navigation
            router.visit(result.url);
        }
        closeDropdown();
        clearSearch();
    };

    // Close dropdown
    const closeDropdown = () => {
        isDropdownOpen.value = false;
        selectedIndex.value = -1;
        if (inputRef.value?.$el && typeof inputRef.value.$el.blur === 'function') {
            inputRef.value.$el.blur();
        }
    };

    // Clear search
    const handleClear = () => {
        clearSearch();
        closeDropdown();
        inputRef.value?.$el?.focus();
    };

    // Click outside handler
    const handleClickOutside = (event: Event) => {
        const target = event.target as HTMLElement;
        if (!target.closest('.global-search-container')) {
            closeDropdown();
        }
    };

    // Global keyboard shortcut (Ctrl/Cmd + K)
    const handleGlobalKeyDown = (event: KeyboardEvent) => {
        if ((event.ctrlKey || event.metaKey) && event.key === 'k') {
            event.preventDefault();
            inputRef.value?.$el?.focus();
        }
    };

    // Get type color for badges
    const getTypeColor = (type: string) => {
        const colors = {
            Company: 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-300',
            User: 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-300',
            Employee: 'bg-purple-100 text-purple-800 dark:bg-purple-900 dark:text-purple-300',
            Voucher: 'bg-orange-100 text-orange-800 dark:bg-orange-900 dark:text-orange-300',
            Navigation: 'bg-indigo-100 text-indigo-800 dark:bg-indigo-900 dark:text-indigo-300',
        };
        return (
            colors[type as keyof typeof colors] ||
            'bg-gray-100 text-gray-800 dark:bg-gray-800 dark:text-gray-300'
        );
    };

    // Get current selection state for an item
    const getItemSelectionClass = (resultIndex: number, contextIndex: number = 0) => {
        let currentIndex = 0;
        for (let i = 0; i < resultIndex; i++) {
            const result = searchResults.value?.results?.[i];
            currentIndex += result?.contexts?.length || 1;
        }
        currentIndex += contextIndex;

        return currentIndex === selectedIndex.value;
    };

    onMounted(() => {
        document.addEventListener('click', handleClickOutside);
        document.addEventListener('keydown', handleGlobalKeyDown);
    });

    onUnmounted(() => {
        document.removeEventListener('click', handleClickOutside);
        document.removeEventListener('keydown', handleGlobalKeyDown);
    });

    watch(
        [searchResults, hasResults, isLoading, isFetching],
        ([results, hasRes, loading, fetching]) => {
            console.log('Search State:', {
                results,
                hasResults: hasRes,
                isLoading: loading,
                isFetching: fetching,
                searchQuery: searchQuery.value,
                isDropdownOpen: isDropdownOpen.value,
            });
        },
        { deep: true },
    );

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createElementBlock("div", _hoisted_1, [
    _createElementVNode("div", _hoisted_2, [
      _createVNode(_unref(Input), {
        ref_key: "inputRef",
        ref: inputRef,
        "model-value": _unref(searchQuery),
        placeholder: _unref(t)('global-search.placeholder'),
        class: "placeholder:text-muted-foreground h-11 w-full !border-none pr-8 pl-9 text-sm !shadow-none",
        onInput: handleInput,
        onKeydown: handleKeyDown,
        onFocus: _cache[0] || (_cache[0] = ($event: any) => (_unref(searchQuery).length >= 2 && (isDropdownOpen.value = true)))
      }, null, 8, ["model-value", "placeholder"]),
      _createVNode(_unref(Search), { class: "text-muted-foreground pointer-events-none absolute left-3 size-4" }),
      (_unref(searchQuery))
        ? (_openBlock(), _createElementBlock("button", {
            key: 0,
            onClick: handleClear,
            class: "text-muted-foreground hover:text-foreground absolute right-3 size-4 cursor-pointer"
          }, [
            _createVNode(_unref(X), { class: "size-4" })
          ]))
        : _createCommentVNode("", true)
    ]),
    (isDropdownOpen.value && (_unref(isFetching) || _unref(hasResults) || (_unref(isMinimumQuery) && !_unref(isFetching))))
      ? (_openBlock(), _createBlock(_unref(Card), {
          key: 0,
          class: "absolute top-full right-0 left-0 z-50 mt-1 max-h-96 overflow-hidden border shadow-lg"
        }, {
          default: _withCtx(() => [
            _createVNode(_unref(CardContent), { class: "p-2" }, {
              default: _withCtx(() => [
                (_unref(isFetching))
                  ? (_openBlock(), _createElementBlock("div", _hoisted_3, [
                      _createVNode(_unref(LoaderCircle), { class: "h-4 w-4 animate-spin" }),
                      _createElementVNode("span", _hoisted_4, _toDisplayString(_unref(t)('global-search.searching')), 1)
                    ]))
                  : (_unref(hasResults))
                    ? (_openBlock(), _createElementBlock("div", {
                        key: 1,
                        ref_key: "resultsRef",
                        ref: resultsRef,
                        class: "max-h-80 overflow-y-auto"
                      }, [
                        (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(_unref(searchResults)?.results || [], (result, resultIndex) => {
                          return (_openBlock(), _createElementBlock(_Fragment, {
                            key: `${result.type}-${result.id}`
                          }, [
                            (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(result.contexts, (context, contextIndex) => {
                              return (_openBlock(), _createBlock(GlobalSearchResult, {
                                key: `${result.type}-${result.id}-${contextIndex}`,
                                result: result,
                                context: context,
                                "context-index": contextIndex,
                                "result-index": resultIndex,
                                "search-query": _unref(searchQuery),
                                "is-selected": getItemSelectionClass(resultIndex, contextIndex),
                                onNavigate: navigateToResult
                              }, null, 8, ["result", "context", "context-index", "result-index", "search-query", "is-selected"]))
                            }), 128))
                          ], 64))
                        }), 128))
                      ], 512))
                    : (_unref(isMinimumQuery) && !_unref(isFetching))
                      ? (_openBlock(), _createElementBlock("div", _hoisted_5, _toDisplayString(_unref(t)('global-search.no_results')), 1))
                      : _createCommentVNode("", true)
              ]),
              _: 1
            })
          ]),
          _: 1
        }))
      : _createCommentVNode("", true)
  ]))
}
}

})